import { UnserializedAndroidGooglePayPaymentSuccessResponse } from '@eventbrite/checkout-native-api';
import logger from '@eventbrite/client-logger';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import {
    client as braintreeClient,
    GooglePayment,
    googlePayment,
} from 'braintree-web';
import { Dispatch } from 'redux';
import { change, setSubmitFailed, stopSubmit, submit } from 'redux-form';
import { trackGooglePayConfirmed } from '../actions/eventAnalytics';
import { CHECKOUT_FORM_NAME, TICKETS_PAGE_FORM_NAME } from '../constants';
import {
    GOOGLE_PAY_MERCHANT_ID,
    GOOGLE_PAY_MERCHANT_NAME,
    GOOGLE_PAY_SHARED_PAYLOAD,
} from '../constants/googlePay';
import { BUYER_ID } from '../models/survey';
import { getDefaultUserCountry, isNativeApp } from '../selectors/app';
import {
    getGooglePayInstance,
    isExpressGooglePayEnabled,
    isGooglePayScriptLoaded,
    TLDSupportsGooglePay,
} from '../selectors/googlePay';
import { getbraintreeClientToken, getTotalAmount } from '../selectors/order';
import { isTicketSelectionFullSizePage } from '../selectors/routing';
import { CheckoutState } from '../state';
import { EventNamespace } from '../state/event';
import { GooglePayScriptStatus } from '../state/googlePay';
import { OrderNamespace } from '../state/order';
import { browserSupportsGooglePay } from '../utils/browser';
import { clearNonceInForm } from '../utils/expressCheckout';
import {
    canMakePaymentsWithGooglePay,
    getGooglePayBuyerData,
    isGooglePayIntegrationSupportedInNativeApp,
    isReadyToPayInAndroidApp,
    makePaymentInAndroidApp,
} from '../utils/googlePay';
import { loadGooglePayScript } from '../utils/googleScriptLoader';
import { getAndSetBraintreeToken } from './order/getAndSetBraintreeToken';

export enum GooglePayActionType {
    setCanMakePaymentsWithGooglePay = 'setCanMakePaymentsWithGooglePay',
    setGooglePayInstance = 'setGooglePayInstance',
    setGooglePayPaymentRequest = 'setGooglePayPaymentRequest',
    setHasErrorInGooglePay = 'setHasErrorInGooglePay',
    setIsInitializingGooglePayPaymentRequest = 'setIsInitializingGooglePayPaymentRequest',
    setIsMakingGooglePayPayment = 'setIsMakingGooglePayPayment',
    setGooglePayScriptStatus = 'setGooglePayScriptStatus',
}

export interface SetCanMakePaymentsWithGooglePayAction {
    type: GooglePayActionType.setCanMakePaymentsWithGooglePay;
    payload: {
        canMakePaymentsWithGooglePay: boolean;
    };
}

export interface SetGooglePayInstanceAction {
    type: GooglePayActionType.setGooglePayInstance;
    payload: {
        googlePayInstance: GooglePayment;
    };
}

export interface SetGooglePayPaymentRequestAction {
    type: GooglePayActionType.setGooglePayPaymentRequest;
    payload: {
        googlePayPaymentRequest: google.payments.api.PaymentDataRequest;
    };
}

export interface SetGooglePayScriptStatusAction {
    type: GooglePayActionType.setGooglePayScriptStatus;
    payload: {
        googlePayScriptStatus: GooglePayScriptStatus;
    };
}

export interface SetHasErrorInGooglePayAction {
    type: GooglePayActionType.setHasErrorInGooglePay;
    payload: {
        hasError: boolean;
    };
}

export interface SetIsInitializingGooglePayPaymentRequestAction {
    type: GooglePayActionType.setIsInitializingGooglePayPaymentRequest;
    payload: {
        isInitializingGooglePayPaymentRequest: boolean;
    };
}

export interface SetIsMakingGooglePayPaymentAction {
    type: GooglePayActionType.setIsMakingGooglePayPayment;
    payload: {
        isMakingGooglePayPayment: boolean;
    };
}

export const setCanMakePaymentsWithGooglePay = (
    canMakePaymentsWithGooglePay: boolean,
): SetCanMakePaymentsWithGooglePayAction => ({
    type: GooglePayActionType.setCanMakePaymentsWithGooglePay,
    payload: {
        canMakePaymentsWithGooglePay,
    },
});

export const setGooglePayInstance = (
    googlePayInstance: GooglePayment,
): SetGooglePayInstanceAction => ({
    type: GooglePayActionType.setGooglePayInstance,
    payload: {
        googlePayInstance,
    },
});

export const setGooglePayPaymentRequest = (
    googlePayPaymentRequest: google.payments.api.PaymentDataRequest,
): SetGooglePayPaymentRequestAction => ({
    type: GooglePayActionType.setGooglePayPaymentRequest,
    payload: {
        googlePayPaymentRequest,
    },
});

export const setGooglePayScriptStatus = (
    googlePayScriptStatus: GooglePayScriptStatus,
): SetGooglePayScriptStatusAction => ({
    type: GooglePayActionType.setGooglePayScriptStatus,
    payload: {
        googlePayScriptStatus,
    },
});

export const setHasErrorInGooglePay = (
    hasError: boolean,
): SetHasErrorInGooglePayAction => ({
    type: GooglePayActionType.setHasErrorInGooglePay,
    payload: {
        hasError,
    },
});

export const setIsInitializingGooglePayPaymentRequest = (
    isInitializingGooglePayPaymentRequest: boolean,
): SetIsInitializingGooglePayPaymentRequestAction => ({
    type: GooglePayActionType.setIsInitializingGooglePayPaymentRequest,
    payload: {
        isInitializingGooglePayPaymentRequest,
    },
});

export const setIsMakingGooglePayPayment = (
    isMakingGooglePayPayment: boolean,
): SetIsMakingGooglePayPaymentAction => ({
    type: GooglePayActionType.setIsMakingGooglePayPayment,
    payload: {
        isMakingGooglePayPayment,
    },
});

const createGooglePayPaymentRequest = async (
    state: CheckoutState,
): Promise<google.payments.api.PaymentDataRequest> => {
    const { event } = state as {
        event: EventNamespace;
    };

    const googlePayInstance = getGooglePayInstance(state) as GooglePayment;

    const paymentRequest = {
        ...GOOGLE_PAY_SHARED_PAYLOAD,
        merchantInfo: {
            merchantId: GOOGLE_PAY_MERCHANT_ID,
            merchantName: GOOGLE_PAY_MERCHANT_NAME,
        },
        transactionInfo: {
            countryCode: getDefaultUserCountry(state),
            currencyCode: event.currency || 'USD',
            totalPriceLabel: `Tickets for ${event.name.text}`,
            totalPrice: `${getTotalAmount(state)}`,
            totalPriceStatus: 'FINAL',
        },
    };

    return googlePayInstance.createPaymentDataRequest(paymentRequest);
};

const createGooglePayInstance = async (order: OrderNamespace) => {
    const braintreeToken = order.braintreeClientToken;
    const client = await braintreeClient.create({
        authorization: braintreeToken,
    });
    return googlePayment.create({
        client,
        googlePayVersion: 2,
        googleMerchantId: GOOGLE_PAY_MERCHANT_ID,
    });
};

export const updateGooglePayPaymentRequest =
    () =>
    async (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
    ) => {
        const state = getState();

        const googlePayPaymentRequest = await createGooglePayPaymentRequest(
            state,
        );

        dispatch(setGooglePayPaymentRequest(googlePayPaymentRequest));
    };

export const initializeGooglePayPaymentRequestInWeb =
    () =>
    async (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
    ) => {
        const state = getState();

        if (isNativeApp(state)) {
            return;
        }

        dispatch(setIsInitializingGooglePayPaymentRequest(true));

        try {
            await dispatch(getAndSetBraintreeToken());

            const googlePayInstance = await createGooglePayInstance(
                state.order as OrderNamespace,
            );
            dispatch(setGooglePayInstance(googlePayInstance));

            await dispatch(updateGooglePayPaymentRequest());
        } catch (error) {
            logger.error(error);
        } finally {
            dispatch(setIsInitializingGooglePayPaymentRequest(false));
        }
    };

export const initializeGooglePayInAndroidApp = async (
    _: Dispatch<CheckoutState>,
    getState: () => CheckoutState,
) => {
    try {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        window.android!.initPayment(getbraintreeClientToken(getState()));
    } catch (e) {
        logger.error(e);
    }
};

export const payWithGooglePayInWeb =
    (paymentData: google.payments.api.PaymentData, instance: GooglePayment) =>
    async (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
    ) => {
        try {
            const response = await instance.parseResponse(paymentData);
            clearNonceInForm(dispatch);

            dispatch(
                change(
                    CHECKOUT_FORM_NAME,
                    'payment.googlePayBraintreeNonce',
                    response?.nonce,
                ),
            );

            const state = getState();
            const isTSPage = isTicketSelectionFullSizePage(state);

            if (isTSPage) {
                dispatch(setIsMakingGooglePayPayment(true));

                dispatch(
                    change(
                        TICKETS_PAGE_FORM_NAME,
                        'payment.googlePayBraintreeNonce',
                        response?.nonce,
                    ),
                );

                dispatch(
                    change(
                        CHECKOUT_FORM_NAME,
                        BUYER_ID,
                        getGooglePayBuyerData(paymentData, state),
                    ),
                );

                return dispatch(submit(TICKETS_PAGE_FORM_NAME));
            }

            dispatch(trackGooglePayConfirmed());
            dispatch(submit(CHECKOUT_FORM_NAME));
        } catch (e) {
            logger.error(e);
        }
    };

export const payWithGooglePayInAndroidApp =
    () =>
    async (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
    ) => {
        makePaymentInAndroidApp(
            getState(),
            async (jsonResponse) => {
                const parsedResponse = JSON.parse(
                    jsonResponse,
                ) as UnserializedAndroidGooglePayPaymentSuccessResponse;

                const { email, paymentMethodData } = parsedResponse;
                const { nonce } =
                    paymentMethodData.tokenizationData.token.androidPayCards[0];

                const paymentData: Parameters<typeof getGooglePayBuyerData>[0] =
                    {
                        email,
                        paymentMethodData,
                    };

                dispatch(
                    change(
                        CHECKOUT_FORM_NAME,
                        'payment.googlePayBraintreeNonce',
                        nonce,
                    ),
                );

                const state = getState();
                const isTSPage = isTicketSelectionFullSizePage(state);

                if (isTSPage) {
                    dispatch(setIsMakingGooglePayPayment(true));

                    dispatch(
                        change(
                            TICKETS_PAGE_FORM_NAME,
                            'payment.googlePayBraintreeNonce',
                            nonce,
                        ),
                    );

                    dispatch(
                        change(
                            CHECKOUT_FORM_NAME,
                            BUYER_ID,
                            getGooglePayBuyerData(paymentData, state),
                        ),
                    );

                    return dispatch(submit(TICKETS_PAGE_FORM_NAME));
                }

                dispatch(trackGooglePayConfirmed());
                dispatch(submit(CHECKOUT_FORM_NAME));
            },
            (error: string) => {
                logger.error(error);
            },
        );
    };

export const initializeGooglePayIfExpressCheckout =
    () =>
    async (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
    ) => {
        if (isExpressGooglePayEnabled(getState())) {
            dispatch(initializeGooglePaySDKIfNeededAction());
        }
    };

export const initializeGooglePaySDKIfNeededAction =
    () => async (dispatch: Dispatch<any>, getState: () => CheckoutState) => {
        const state = getState();
        const {
            app: { isProd = false },
        } = state;

        await dispatch(getAndSetBraintreeToken());

        if (
            isGooglePayScriptLoaded(state) ||
            !browserSupportsGooglePay() ||
            !TLDSupportsGooglePay(state)
        ) {
            return;
        }

        loadGooglePayScript({
            onScriptLoad: async () => {
                dispatch(
                    setGooglePayScriptStatus(GooglePayScriptStatus.loaded),
                );

                const isInNativeApp = isNativeApp(state);

                if (!isInNativeApp) {
                    const canMakePayments = (
                        await canMakePaymentsWithGooglePay(isProd)
                    )?.result;

                    dispatch(setCanMakePaymentsWithGooglePay(canMakePayments));
                }

                if (
                    !isInNativeApp &&
                    !isGooglePayIntegrationSupportedInNativeApp()
                ) {
                    return;
                }

                await initializeGooglePayInAndroidApp(dispatch, getState);

                isReadyToPayInAndroidApp(
                    (canMakePayments) => {
                        dispatch(
                            setCanMakePaymentsWithGooglePay(canMakePayments),
                        );
                    },
                    (error) => logger.error(error),
                );
            },
            onScriptLoadError: () => {
                dispatch(
                    setGooglePayScriptStatus(GooglePayScriptStatus.failed),
                );
                logger.error('Error loading Google Pay script');
            },
        });
    };

export const handleOnGooglePayError =
    (error: google.payments.api.PaymentsError | Error) =>
    (dispatch: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        const LOCALIZED_ERROR_MESSAGE_BY_RAW_ERROR_MESSAGE: Record<
            string,
            GenericLazyString
        > = {
            'Can not open popup window - blocked': gettext(
                'GooglePay popup could not be opened. Please, enable popups to pay with GooglePay.',
            ),
        };
        const DEFAULT_LOCALIZED_ERROR_MESSAGE = gettext(
            'There was an error connecting to GooglePay.',
        );
        const rawErrorMessage =
            error instanceof Error ? error.message : error.statusMessage;

        const isTSPage = isTicketSelectionFullSizePage(getState());

        const localizedErrorMessage =
            LOCALIZED_ERROR_MESSAGE_BY_RAW_ERROR_MESSAGE[rawErrorMessage] ||
            DEFAULT_LOCALIZED_ERROR_MESSAGE;

        if (isTSPage) {
            dispatch(
                stopSubmit(TICKETS_PAGE_FORM_NAME, {
                    _error: localizedErrorMessage,
                }),
            );

            dispatch(setSubmitFailed(TICKETS_PAGE_FORM_NAME));
        } else {
            dispatch(
                stopSubmit(CHECKOUT_FORM_NAME, {
                    _error: localizedErrorMessage,
                }),
            );
            dispatch(setSubmitFailed(CHECKOUT_FORM_NAME));
        }
    };

export type GooglePayAction =
    | SetCanMakePaymentsWithGooglePayAction
    | SetGooglePayInstanceAction
    | SetGooglePayPaymentRequestAction
    | SetGooglePayScriptStatusAction
    | SetHasErrorInGooglePayAction
    | SetIsInitializingGooglePayPaymentRequestAction
    | SetIsMakingGooglePayPaymentAction;
